/**
 * Reusable Rules Modal Module
 */

import { launchTutorial } from './krida-tutorial.js';

// Module-level variables to hold references
let rulesModalElement = null;
let rulesModalBody = null;
let rulesContentLoaded = false;
let isInitialized = false;

/**
 * Fetches the modal HTML, injects it, fetches rules content, and sets up listeners.
 * Should be called once on page load (e.g., DOMContentLoaded).
 */
async function initRulesModal() {
    if (isInitialized) {
        console.log("Rules Modal already initialized.");
        return;
    }
    console.log("Initializing Shared Rules Modal...");

    try {
        // 1. Fetch and Inject Modal HTML (if not already present)
        if (!document.getElementById('rules-modal')) {
            const response = await fetch('/rules-modal.html'); // Path updated
            if (!response.ok) throw new Error(`Failed to fetch rules modal structure: ${response.status}`);
            const modalHtml = await response.text();
            // Inject into body
            document.body.insertAdjacentHTML('beforeend', modalHtml);
            console.log("Rules modal HTML injected.");
        }
        
        // 2. Get Element References
        rulesModalElement = document.getElementById('rules-modal');
        if (!rulesModalElement) throw new Error("Rules modal element not found after injection.");
        rulesModalBody = rulesModalElement.querySelector('.modal-body');
        const closeButton = rulesModalElement.querySelector('.close-button');
        
        if (!rulesModalBody) throw new Error("Rules modal body not found.");
        if (!closeButton) throw new Error("Rules modal close button not found.");

        // 3. Fetch Rules Content (only once)
        if (!rulesContentLoaded) {
            try {
                const contentResponse = await fetch('/rules.html');
                if (!contentResponse.ok) throw new Error(`Failed to fetch rules content: ${contentResponse.status}`);
                const rulesHtml = await contentResponse.text();
                
                // Basic check to avoid inserting full HTML page structure if rules.html is a full page
                const parser = new DOMParser();
                const doc = parser.parseFromString(rulesHtml, 'text/html');
                const bodyContent = doc.body.innerHTML;
                rulesModalBody.innerHTML = bodyContent || rulesHtml;
                rulesContentLoaded = true;
                console.log("Rules content loaded into modal.");

                // Wire up the "Watch Tutorial" button
                const watchTutorialBtn = rulesModalBody.querySelector('#rules-watch-tutorial-btn');
                if (watchTutorialBtn) {
                    watchTutorialBtn.addEventListener('click', () => {
                        closeRulesModal();
                        launchTutorial('quickStart');
                    });
                }
            } catch (contentError) {
                console.error("Error loading rules content:", contentError);
                rulesModalBody.innerHTML = '<p>Could not load rules. Please try again later.</p>';
                rulesContentLoaded = false; // Allow retry later?
            }
        }

        // 4. Attach Close Listener
        closeButton.addEventListener('click', closeRulesModal);
        // Optional: Close on background click
        rulesModalElement.addEventListener('click', (event) => {
            if (event.target === rulesModalElement) {
                closeRulesModal();
            }
        });

        isInitialized = true;
        console.log("Shared Rules Modal Initialized Successfully.");

    } catch (error) {
        console.error("Error initializing shared rules modal:", error);
        isInitialized = false;
        // Optional: Display error to user?
    }
}

/**
 * Opens the Rules Modal.
 */
async function openRulesModal() {
    if (!isInitialized || !rulesModalElement) {
        console.warn("Rules modal not initialized yet. Attempting to initialize now...");
        await initRulesModal(); // Wait for initialization to complete
        // Re-check if initialization succeeded
        if (!isInitialized || !rulesModalElement) {
            console.error("Cannot open rules modal: Initialization failed.");
            // Import and use showErrorModal for consistent error handling
            try {
                const { showErrorModal } = await import('./krida-ui-error.js');
                showErrorModal("Rules Error", "Could not load game rules. Please try again.");
            } catch (e) {
                // Fallback to alert if import fails
                alert("Rules display failed to load. Please try again later.");
            }
            return;
        }
    }

    // If we reach here, initialization is complete
    console.log("Opening rules modal...");
    rulesModalElement.style.display = 'block'; // Or 'flex' if using modal-fullscreen-center
}

/**
 * Closes the Rules Modal.
 */
function closeRulesModal() {
    if (rulesModalElement) {
        rulesModalElement.style.display = 'none';
    }
}

// Also export for potential future use by actual modules
export { initRulesModal, openRulesModal, closeRulesModal }; 